import React from 'react';
import { BookOpen, Award, Heart } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal, useCountUp } from '../hooks/useAnimations';

export default function AboutSection() {
  const { t } = useLanguage();
  const [sectionRef, isVisible] = useScrollReveal(0.15);
  const [imgRef, imgVisible] = useScrollReveal(0.15);
  const papers = useCountUp(25, 1500, isVisible);
  const years = useCountUp(10, 1200, isVisible);

  return (
    <section id="about" className="py-20 md:py-32 bg-[#FAFAFA]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Doctor Images */}
          <div
            ref={imgRef}
            className={`relative transition-all duration-1000 ${
              imgVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
            data-testid="about-doctor-image"
          >
            <div className="space-y-4">
              {/* Main desk portrait — large */}
              <div className="bg-gradient-to-br from-[#FDF1EC]/60 to-[#F0F9FF]/40 rounded-3xl p-6 lg:p-8">
                <img
                  src="https://customer-assets.emergentagent.com/job_kuldeep-skin/artifacts/4bwlb3o3_WhatsApp%20Image%202026-04-12%20at%2014.02.50.jpeg"
                  alt="Dr. Kuldeep Verma - Best Dermatologist and Hair Transplant Surgeon in Jhansi"
                  className="w-full rounded-2xl shadow-[0_12px_40px_rgb(0,0,0,0.1)] object-cover aspect-[4/3] hover:scale-[1.01] transition-transform duration-500"
                />
              </div>
              {/* Secondary — consultation image */}
              <div className="rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                <img
                  src="https://customer-assets.emergentagent.com/job_kuldeep-skin/artifacts/dzwqjywm_WhatsApp%20Image%202026-04-12%20at%2014.35.23.jpeg"
                  alt="Dr. Kuldeep Verma examining patient at Divyadeep Skin Clinic Jhansi"
                  className="w-full rounded-2xl object-cover aspect-[16/9] hover:scale-[1.01] transition-transform duration-500"
                />
              </div>
            </div>
            {/* Floating badge */}
            <div className="mt-4 lg:mt-0 lg:absolute lg:-bottom-4 lg:left-auto lg:translate-x-0 lg:-right-4 mx-auto lg:mx-0 w-fit bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.08)] px-6 py-4 flex items-center gap-3 lg:animate-[float_4s_ease-in-out_infinite]">
              <div className="w-10 h-10 rounded-xl bg-[#D1A760]/10 flex items-center justify-center">
                <Award className="w-5 h-5 text-[#D1A760]" />
              </div>
              <div>
                <p className="font-outfit font-medium text-[#0F172A] text-sm">IADVL Fellow</p>
                <p className="text-xs text-[#475569] font-manrope">Lasers & Hair Transplant</p>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div
            ref={sectionRef}
            className={`space-y-8 transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
          >
            <div>
              <p className="text-sm font-manrope font-medium text-[#D1A760] tracking-wide uppercase mb-3">About</p>
              <h2 className="font-outfit font-light text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] tracking-tight" data-testid="about-title">
                {t('about_title')}
              </h2>
              <p className="mt-2 font-manrope text-sm text-[#475569]">{t('about_credentials')}</p>
            </div>

            <p className="text-base text-[#475569] font-manrope leading-relaxed" data-testid="about-description">
              {t('about_desc')}
            </p>

            {/* Animated stats inline */}
            <div className="flex gap-8">
              <div className="text-center">
                <p className="font-outfit font-semibold text-3xl text-[#D1A760]">{papers}+</p>
                <p className="text-xs text-[#475569] font-manrope">Research Papers</p>
              </div>
              <div className="w-px bg-slate-200" />
              <div className="text-center">
                <p className="font-outfit font-semibold text-3xl text-[#0F172A]">{years}+</p>
                <p className="text-xs text-[#475569] font-manrope">Years Experience</p>
              </div>
            </div>

            <div className="space-y-4">
              {[
                { icon: BookOpen, color: 'bg-[#F0F9FF]', iconColor: 'text-blue-500', title: t('about_research'), sub: 'Published in national & international journals' },
                { icon: Award, color: 'bg-[#FDF1EC]', iconColor: 'text-[#D1A760]', title: t('about_experience'), sub: 'IGMC Shimla & BJMC Ahmedabad trained' },
                { icon: Heart, color: 'bg-green-50', iconColor: 'text-green-500', title: t('about_personalized'), sub: 'Because no two skins are the same' },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className={`flex items-start gap-4 transition-all duration-500 ${
                    isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                  }`}
                  style={{ transitionDelay: `${400 + idx * 150}ms` }}
                >
                  <div className={`w-10 h-10 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform duration-200`}>
                    <item.icon className={`w-5 h-5 ${item.iconColor}`} />
                  </div>
                  <div>
                    <p className="font-outfit font-medium text-[#0F172A] text-sm">{item.title}</p>
                    <p className="text-xs text-[#475569] font-manrope">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            <blockquote className="border-l-2 border-[#D1A760] pl-6 py-2" data-testid="about-tagline">
              <p className="font-outfit text-lg text-[#0F172A] italic">
                "{t('about_tagline')}"
              </p>
            </blockquote>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(-50%); }
          50% { transform: translateY(-8px) translateX(-50%); }
        }
        @media (min-width: 1024px) {
          @keyframes float {
            0%, 100% { transform: translateY(0) translateX(0); }
            50% { transform: translateY(-8px) translateX(0); }
          }
        }
      `}</style>
    </section>
  );
}
